import { useQuery } from "@tanstack/react-query";
import { Card, Col, Form, Row, Select, Switch, Tooltip } from "antd";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { getAssignedClientForInfraConfig } from "../../../../api/apiCallManager";
import useInfraFormData from "../../../../components/hooks/useInfraFormData";
import Ec2FormTable from "./Ec2FormTable";
import ServerInfraTable from "./ServerInfraTable";
import ClientInfraTable from "./ClientInfraTable";

const { Option } = Select;

const Ec2InfraTypeSwitch = () => {
  const [form] = Form.useForm();
  const [instanceCategory, setInstanceCategory] = useState(true);
  const [selectedInfraClient, setSelectedInfraClient] = useState("");
  const [checked, setChecked] = useState(false);
  const { id } = useParams();

  // Query to fetch Infra Form Data
  const {
    data: infraFormData,
    error: infraFormError,
    isLoading: infraFormLoading,
  } = useInfraFormData(id);

  // Query to fetch Assigned Clients Data
  const {
    data: assignedClients,
    error: assignedClientsError,
    isLoading: isLoadingClients,
  } = useQuery({
    queryKey: ["assignedClientInfraConfig_"],
    queryFn: getAssignedClientForInfraConfig,
  });

  // Create the list of clients for the Select dropdown using the fetched data
  const getInfraClientList = () => {
    return assignedClients?.map((val, i) => ({
      label: (
        <div className="flex justify-between">
          <p>{val.clientName}</p> {val.infraStatus && <p>✅</p>}
        </div>
      ),
      value: val.clientName,
      disabled: val.infraStatus,
    }));
  };

  // Check if all clients have infraStatus set
  const allInfraStatusSet = assignedClients?.every((val) => !!val.infraStatus);

  // Handle switching between Server and Client
  const SwitchOnchange = (checked) => {
    setInstanceCategory(checked); // Set to true for Server, false for Client
  };

  // Handle client selection from the dropdown
  const handleClientSelect = (value) => {
    const selectedClient = assignedClients.find(
      (client) => client.clientName === value
    );
    setSelectedInfraClient(selectedClient);
  };

  // Separate server and client configuration data
  const serverInfraConfigData = infraFormData?.filter(
    (item) => item.infraType === "server"
  );
  const clientInfraConfigData = infraFormData?.filter(
    (item) => item.infraType === "client"
  );

  // Handle the client switch toggle
  const handleChange = (checked) => {
    setChecked(checked);
  };

  // Render the server instance configuration
  const renderServerInstanceConfig = () => {
    if (serverInfraConfigData?.length > 0) {
      return <ServerInfraTable serverInfraConfigData={serverInfraConfigData} />;
    }
    return (
      <Ec2FormTable
        setChecked={setChecked}
        selectedInfraClient={selectedInfraClient}
        instanceCategory={instanceCategory}
      />
    );
  };

  // Render client selection dropdown if not all infraStatus are set
  const renderClientSelect = () => {
    if (!allInfraStatusSet) {
      return (
        <>
          Clients:{" "}
          <Select
            className="w-[300px]"
            placeholder="Select a client"
            options={getInfraClientList()}
            onChange={handleClientSelect}
            loading={isLoadingClients} // Loading state while fetching clients
          />
        </>
      );
    }
    return null;
  };

  // Render client switch with tooltip
  const renderClientSwitch = () => {
    if (clientInfraConfigData?.length > 0 && !allInfraStatusSet) {
      return (
        <Tooltip
          placement="top"
          title={checked ? "Configured Clients List" : ""}
        >
          <Switch checked={checked} onChange={handleChange} />
        </Tooltip>
      );
    }
    return null;
  };

  // Render client instance configuration based on the state
  const renderClientInstanceConfig = () => {
    if (checked || allInfraStatusSet) {
      return <ClientInfraTable clientInfraConfigData={clientInfraConfigData} />;
    }
    if (!allInfraStatusSet) {
      return (
        <Ec2FormTable
          setChecked={setChecked}
          selectedInfraClient={selectedInfraClient}
          instanceCategory={instanceCategory}
        />
      );
    }
    return null;
  };

  // Render the complete component
  return (
    <div className="py-2 px-3">
      <Form.Item label="Instance Category">
        <Switch
          checkedChildren="Server"
          unCheckedChildren="Client"
          defaultChecked
          onChange={SwitchOnchange}
        />
      </Form.Item>
      <Card
        title={`Configure ${instanceCategory ? "Server" : "Client"} Instance`}
        bordered={false}
      >
        {instanceCategory ? (
          renderServerInstanceConfig()
        ) : (
          <>
            <Row gutter={16} className="mb-5">
              <Col lg={20}>{renderClientSelect()}</Col>
              <Col lg={4} className="text-end">
                {renderClientSwitch()}
              </Col>
            </Row>
            {renderClientInstanceConfig()}
          </>
        )}
      </Card>
    </div>
  );
};

export default Ec2InfraTypeSwitch;
